import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

const slides = [
  {
    image: "/gallery/cover.png",
    kicker: "Planned to Perfection",
    title: ["We Make Your", "Celebrations Royal"],
    sub: "From the first digital invite to the final Vidai — a one-stop solution for unforgettable weddings across India.",
  },
  {
    image: "/gallery/g2.png",
    kicker: "Decor & Design",
    title: ["Breathtaking Stages", "& Floral Artistry"],
    sub: "Stunning floral arrangements, bespoke stage fabrication and thematic lighting crafted around your story.",
  },
  {
    image: "/gallery/g5.png",
    kicker: "Grand Celebrations",
    title: ["Timeless Events,", "Flawlessly Executed"],
    sub: "Weddings, engagements, corporate galas and grand parties — delivered with precision, warmth and flair.",
  },
];

export default function Hero() {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 6000);
    return () => clearInterval(t);
  }, []);

  const cur = slides[idx];
  const prev = () => setIdx((i) => (i - 1 + slides.length) % slides.length);
  const next = () => setIdx((i) => (i + 1) % slides.length);

  return (
    <section id="top" data-testid="hero-section" className="relative h-[100svh] min-h-[640px] overflow-hidden bg-heirloom-emerald">
      {/* background slider */}
      <AnimatePresence>
        <motion.div
          key={idx}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ opacity: { duration: 1.2 }, scale: { duration: 7, ease: "linear" } }}
          className="absolute inset-0 pointer-events-none"
        >
          <img src={cur.image} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </motion.div>
      </AnimatePresence>

      {/* content */}
      <div className="relative z-10 h-full max-w-[1300px] mx-auto px-6 flex flex-col justify-center">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="mb-6">
          <span className="gold-rule overline text-heirloom-gold-light">Your Imagination Is Our Creation</span>
        </motion.div>
        <AnimatePresence mode="wait">
          <motion.div key={idx} className="max-w-2xl">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mb-5 leading-none">
              <span className="font-script text-3xl md:text-4xl text-heirloom-gold-light">{cur.kicker}</span>
            </motion.div>
            <h1 className="font-display text-heirloom-bone font-semibold leading-[1.02] text-[clamp(2.6rem,6.5vw,5.5rem)]">
              {cur.title.map((line, i) => (
                <span key={i} className="block overflow-hidden">
                  <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.2 + i * 0.12, ease: [0.16, 1, 0.3, 1] }}>
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="text-heirloom-bone/85 text-base md:text-lg mt-6 max-w-xl leading-relaxed">
              {cur.sub}
            </motion.p>
          </motion.div>
        </AnimatePresence>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }} className="flex flex-wrap items-center gap-4 mt-9">
          <button data-testid="hero-plan-btn" onClick={() => document.querySelector("#inquiry")?.scrollIntoView({ behavior: "smooth" })} className="bg-heirloom-maroon text-heirloom-bone text-xs font-semibold tracking-[0.18em] uppercase px-9 py-4 rounded-full hover:bg-heirloom-maroon-deep hover:shadow-2xl transition-all duration-300">
            Get In Touch
          </button>
          <button data-testid="hero-work-btn" onClick={() => document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" })} className="border border-heirloom-bone/60 text-heirloom-bone text-xs font-semibold tracking-[0.18em] uppercase px-9 py-4 rounded-full hover:bg-heirloom-bone hover:text-heirloom-maroon transition-all duration-300">
            Our Services
          </button>
        </motion.div>
      </div>

      {/* years badge */}
      <motion.div initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1 }} className="absolute z-10 bottom-28 right-6 md:right-12 hidden sm:flex flex-col items-center justify-center h-32 w-32 md:h-40 md:w-40 rounded-full bg-heirloom-gold text-heirloom-maroon-deep floaty shadow-2xl">
        <span className="font-display text-4xl md:text-5xl font-bold">15+</span>
        <span className="overline text-[0.6rem] mt-1 text-center leading-tight">Years of<br/>Excellence</span>
      </motion.div>

      {/* controls */}
      <div className="absolute z-10 bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-5">
        <button data-testid="hero-prev" onClick={prev} className="text-heirloom-bone/70 hover:text-heirloom-gold-light transition-colors"><ChevronLeft size={22} /></button>
        <div className="flex gap-2">
          {slides.map((_, i) => (
            <button key={i} data-testid={`hero-dot-${i}`} onClick={() => setIdx(i)} className={`h-1.5 rounded-full transition-all duration-400 ${i === idx ? "w-8 bg-heirloom-gold" : "w-2 bg-heirloom-bone/40"}`} />
          ))}
        </div>
        <button data-testid="hero-next" onClick={next} className="text-heirloom-bone/70 hover:text-heirloom-gold-light transition-colors"><ChevronRight size={22} /></button>
      </div>
    </section>
  );
}
